import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../Styles/ManageDrafts.css";

const API_URL = process.env.REACT_APP_API_URL;

// --- Reusable Components ---

const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="pagination-controls">
      <button
        className="pagination-btn"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        aria-label="Go to previous page"
      >
        &lt;
      </button>
      <span>
        Page {page} of {totalPages}
      </span>
      <button
        className="pagination-btn"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Go to next page"
      >
        &gt;
      </button>
    </div>
  );
};

// --- Helper Functions ---

const getClientFY = () => {
  const d = new Date();
  const m = d.getMonth() + 1;
  const y = d.getFullYear();
  return m >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};


// --- Main Component ---

const ManageDrafts = () => {
  // --- State ---
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter & Pagination State
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("ALL");
  const [page, setPage] = useState(1);
  const [totalDrafts, setTotalDrafts] = useState(0);
  const ITEMS_PER_PAGE = 25;

  // Financial Year State
  const [finYear, setFinYear] = useState("");
  const [finYearOptions, setFinYearOptions] = useState([]);

  // --- Hooks ---
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const abortRef = useRef(null);


  // --- Data Fetching (Centralized Logic) ---
  const fetchDrafts = useCallback(
    async (fy, pageNum, search, type) => {
      if (!token || !fy) {
        if (!token) navigate("/login");
        return;
      }
      setLoading(true);

      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      try {
        const url = `${API_URL}/drafts?finyear=${encodeURIComponent(fy)}&page=${pageNum}&limit=${ITEMS_PER_PAGE}&search=${encodeURIComponent(search)}&doctype=${encodeURIComponent(type)}`;

        const res = await axios.get(url, { headers, signal: abortRef.current.signal });

        setDrafts(res.data.items || []);
        setTotalDrafts(res.data.total || 0);

      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error("Failed to load drafts", err);
      } finally {
        setLoading(false);
      }
    },
    [headers, navigate, token, ITEMS_PER_PAGE]
  );


  // --- Effects ---

  // 1. Effect to load Financial Years ONCE on mount
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    const ac = new AbortController();
    axios
      .get(`${API_URL}/financial-years`, { headers, signal: ac.signal })
      .then((res) => {
        const years = Array.isArray(res.data?.years) ? res.data.years : [];
        const current = typeof res.data?.current === "string" ? res.data.current : "";
        setFinYearOptions(years);
        setFinYear(current || years[0] || getClientFY());
      })
      .catch(() => {
        const fallbackFY = getClientFY();
        setFinYearOptions([fallbackFY]);
        setFinYear(fallbackFY);
      });
    return () => ac.abort();
  }, [headers, navigate, token]);


  // 2. Effect to fetch data when filters (search, type, finYear) change (DEBOUNCED)
  useEffect(() => {
    // This effect handles any change in filters by resetting to page 1.
    // The actual fetch for page 1 is triggered by the page-change effect below.
    const handler = setTimeout(() => {
      if (page !== 1) {
        setPage(1);
      } else {
        // If already on page 1, we still need to trigger a refetch with the new filters.
        fetchDrafts(finYear, 1, searchTerm.trim(), filterType);
      }
    }, 300);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterType, finYear]);


  // 3. Effect to fetch data ONLY when the page number changes
  useEffect(() => {
    // This hook is now the single source of truth for fetching.
    // It runs when the page changes, or when the filter effect above resets the page to 1.
    if (!finYear) return;
    fetchDrafts(finYear, page, searchTerm.trim(), filterType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);


  // --- Actions ---
  const handleView = (docno, fy, doctype) => {
    navigate(`/invoices/view/${docno}/${fy}?doctype=${doctype}`);
  };

  const getStatusBadge = (isConverted) =>
    isConverted ? (
      <span className="status-badge converted">Converted</span>
    ) : (
      <span className="status-badge active">Active</span>
    );

  const totalPages = Math.ceil(totalDrafts / ITEMS_PER_PAGE);

  // --- Render ---
  return (
    <div className="manage-drafts-container">
      {/* -- Toolbar Section -- */}
      <div className="drafts-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            className="search-input"
            placeholder="Search by Draft No or Customer"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="toolbar-center">
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>

        <div className="toolbar-right">
          <label className="fy-filter">
            Financial Year:
            <select
              className="fy-select"
              value={finYear}
              onChange={(e) => setFinYear(e.target.value)}
            >
              {finYearOptions.map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
          </label>
          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="ALL">All Types</option>
            <option value="ESTIMATE">Estimate</option>
            <option value="DC">Delivery Challan</option>
          </select>
        </div>
        {loading && <span className="loading-badge">Loading…</span>}
      </div>

      {/* -- Table Section -- */}
      <div className="table-wrapper">
        <table className="drafts-table">
          <thead>
            <tr>
              <th>Doc No</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Type</th>
              <th className="status-col">Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && drafts.length > 0 ? (
              drafts.map((d) => (
                <tr key={`${d.docno}-${d.finyear}-${d.doctype}`}>
                  <td data-label="Doc No">{d.docno}</td>
                  <td data-label="Date">{new Date(d.docdate).toLocaleDateString("en-GB")}</td>
                  <td data-label="Customer">{d.customername}</td>
                  <td data-label="Type">{d.doctype}</td>
                  <td data-label="Status" className="status-cell">
                    {getStatusBadge(d.is_converted)}
                  </td>
                  <td className="actions-col" data-label="Actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleView(d.docno, d.finyear, d.doctype)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="empty-state">
                  {loading ? "Loading drafts..." : "No drafts found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageDrafts;

